import React, { useState, useEffect } from 'react';
import {
    Button,
    Col,
    ConfigProvider,
    Form,
    Input,
    Modal,
    Row,
    Upload,
    Switch,
    Slider,
    Tooltip,
    Select,
    Alert,
    Tabs,
    Table,
    Popconfirm,
    Progress,
    message,
    Badge,
    Tag
} from 'antd';
import { BiCheck, BiX, BiTrash, BiSync, BiFile, BiLink } from "react-icons/bi";
import { UploadOutlined, InfoCircleOutlined, InboxOutlined } from "@ant-design/icons";
import locale from "antd/es/locale/es_ES";
import HomeColors from "../../styles/HomeColors";
import { PostAula } from "../../dao/PostAula";
import { GetUsuaris } from "../../dao/GetUsuaris";
import { GetAulaAdmins } from "../../dao/GetAulaAdmins";
import { AuthContext } from "../../config/AuthContext";
import {
    GetAulaDocuments,
    PostAulaDocument,
    PostAulaUrlDocument,
    DeleteAulaDocument,
    StartBedrockSync,
    GetBedrockSyncStatus
} from "../../dao/AulaDocuments";

interface Props {
    element: any;
    visible: boolean;
    onClose: (updated: boolean, data?: any) => void;
    alumnes: [];
    selectedIds: [];
}

const AulaModal: React.FC<Props> = ({ element, visible, onClose }) => {
    const [form] = Form.useForm();
    const [preview, setPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [iaEnabled, setIaEnabled] = useState<boolean>(false);
    const [profesors, setProfesors] = useState<any[]>([]);

    // Document & KB management states
    const [activeTab, setActiveTab] = useState<string>("1");
    const [documents, setDocuments] = useState<any[]>([]);
    const [docsLoading, setDocsLoading] = useState<boolean>(false);
    const [syncLoading, setSyncLoading] = useState<boolean>(false);
    const [syncStatus, setSyncStatus] = useState<string>("NOT_SYNCED");
    const [syncProgress, setSyncProgress] = useState<number>(0);
    const [urlInput, setUrlInput] = useState<string>("");
    const [urlLoading, setUrlLoading] = useState<boolean>(false);

    const { userId } = React.useContext(AuthContext);

    const normalizeImg = (img?: string | null) => {
        if (!img) return null;
        if (img.startsWith("base64:")) {
            const parts = img.split(":");
            img = parts[parts.length - 1];
        }
        if (img.startsWith("/9j/")) return `data:image/jpeg;base64,${img}`;
        return `data:image/png;base64,${img}`;
    };

    // Fetch documents uploaded to the classroom
    const fetchDocuments = async () => {
        if (!element || element.id === 0 || element.id === "0") return;
        setDocsLoading(true);
        try {
            const docs = await GetAulaDocuments(element.id + "");
            setDocuments(docs || []);
        } catch (error) {
            console.error("Error loading classroom documents:", error);
        } finally {
            setDocsLoading(false);
        }
    };

    useEffect(() => {
        const fetchProfesors = async () => {
            const data = await GetUsuaris();
            if (data) {
                const profes = data.filter((u: any) => u.rol === 1 || u.rol === "1" || u.rol === 0 || u.rol === "0");
                setProfesors(profes);
            }
        };
        fetchProfesors();

        if (element) {
            console.log("AulaModal element (raw):", element);
            let ideArch = 'fargate_standard';
            if (element.ideArchitecture) {
                ideArch = element.ideArchitecture;
            } else if (element.ideLight === 2 || element.ideLight === "2") {
                ideArch = 'ec2_docker';
            } else if (element.ideLight === 1 || element.ideLight === true || element.ideLight === "1") {
                ideArch = 'fargate_light';
            }

            const normalizedElement = {
                ...element,
                ia: element.ia === 1 || element.ia === true,
                dudas: element.dudas === 1 || element.dudas === true,
                colab: element.colab === 1 || element.colab === true,
                copypaste: element.copypaste === 1 || element.copypaste === true,
                dual: element.dual === 1 || element.dual === true,
                inscripcionsObertes: element.inscripcions_obertes === undefined ? true : (element.inscripcions_obertes === 1 || element.inscripcions_obertes === true),
                jocHabilitat: element.jocHabilitat === undefined ? true : (element.jocHabilitat === 1 || element.jocHabilitat === true),
                bloqueoFoco: element.bloqueoFoco === undefined ? true : (element.bloqueoFoco === 1 || element.bloqueoFoco === true),
                isExamen: element.isExamen === undefined ? (element.is_examen === undefined ? false : (element.is_examen === 1 || element.is_examen === true)) : (element.isExamen === 1 || element.isExamen === true),
                ideLight: element.ideLight === 1 || element.ideLight === true,
                ideArchitecture: ideArch,
            };

            if (!normalizedElement.tipo && typeof normalizedElement.tipus === 'string' && 
                ['llama', 'chatgpt', 'agent'].includes(normalizedElement.tipus)) {
                normalizedElement.tipo = normalizedElement.tipus;
            }

            if ((element.id === 0 || element.id === "0") && !normalizedElement.tipo) {
                normalizedElement.tipo = 'llama';
            }

            console.log("AulaModal normalizedElement:", normalizedElement);
            form.setFieldsValue(normalizedElement);
            setPreview(normalizeImg(element.img));
            setIaEnabled(normalizedElement.ia || false);

            const loadAdmins = async () => {
                try {
                    const admins = await GetAulaAdmins(element.id);
                    form.setFieldsValue({ admins: admins });
                } catch (e) {
                    console.log("Error loading admins", e);
                }
            };
            if (element.id !== 0 && element.id !== "0") {
                loadAdmins();
                fetchDocuments();
            } else {
                if (userId) {
                    form.setFieldsValue({ admins: [parseInt(userId)] });
                }
            }

        } else {
            form.resetFields();
            setPreview(null);
            setIaEnabled(false);
        }
        setActiveTab("1"); // Default tab
    }, [element, form, userId]);

    const handleCancel = () => {
        onClose(false);
        setPreview(null);
    };

    async function handleSubmit() {
        setLoading(true);
        try {
            const values = await form.validateFields();
            const imagenBase64 = preview ? (preview.includes(',') ? preview.split(',')[1] : preview) : null;
            
            // Mapeo numérico para BBDD:
            // 0: Fargate Estándar, 1: Fargate Light, 2: EC2 Docker
            let archCode = 0;
            if (values.ideArchitecture === 'fargate_light') {
                archCode = 1;
            } else if (values.ideArchitecture === 'ec2_docker') {
                archCode = 2;
            }

            const payload = { 
                ...values, 
                ideLight: archCode,
                img: imagenBase64 
            };
            await PostAula(element.id + "", payload);
            message.success("Aula guardada correctament!");
            onClose(true, payload);
        } catch (e) {
            console.error("Error saving aula:", e);
        } finally {
            setLoading(false);
        }
    }

    // Handles URL scraping and uploading to the classroom's documents
    const handleUploadUrl = async () => {
        if (!urlInput || !urlInput.trim()) {
            message.warning("Si us plau, introdueix una adreça URL vàlida.");
            return;
        }

        if (!urlInput.trim().startsWith("http://") && !urlInput.trim().startsWith("https://")) {
            message.warning("La URL ha de començar per http:// o https://");
            return;
        }

        setUrlLoading(true);
        try {
            await PostAulaUrlDocument(element.id + "", urlInput.trim());
            message.success("La URL s'ha afegit i processat correctament! 🎉");
            setUrlInput("");
            fetchDocuments();
        } catch (err: any) {
            message.error(err.message || "Error al processar la URL.");
        } finally {
            setUrlLoading(false);
        }
    };

    // Handles document deletion
    const handleDeleteDocument = async (docId: string) => {
        try {
            const success = await DeleteAulaDocument(element.id + "", docId);
            if (success) {
                message.success("Document eliminat correctament!");
                fetchDocuments();
            } else {
                message.error("No s'ha pogut eliminar el document.");
            }
        } catch (error) {
            message.error("Error al eliminar el document.");
        }
    };

    // Handles programmatic bedrock index sync
    const handleSyncBedrock = async () => {
        setSyncLoading(true);
        setSyncStatus("STARTING");
        setSyncProgress(15);
        try {
            const job = await StartBedrockSync(element.id + "");
            if (job && job.jobId) {
                setSyncStatus("IN_PROGRESS");
                setSyncProgress(40);
                
                // Poll sync status every 3 seconds
                const interval = setInterval(async () => {
                    const status = await GetBedrockSyncStatus(element.id + "", job.jobId);
                    setSyncStatus(status);
                    
                    if (status === "COMPLETE") {
                        setSyncProgress(100);
                        message.success("Base de coneixements sincronitzada correctament a AWS! 🚀");
                        setSyncLoading(false);
                        clearInterval(interval);
                    } else if (status === "FAILED" || status === "UNKNOWN") {
                        setSyncProgress(100);
                        message.error("La sincronització ha fallat a AWS Bedrock.");
                        setSyncLoading(false);
                        clearInterval(interval);
                    } else {
                        // Increment progress bar slightly to give dynamic feeling
                        setSyncProgress(prev => Math.min(prev + 10, 95));
                    }
                }, 3000);
            } else {
                message.error("No s'ha pogut iniciar el Job de sincronització a Bedrock.");
                setSyncLoading(false);
                setSyncStatus("FAILED");
            }
        } catch (error) {
            message.error("Error en iniciar la sincronització.");
            setSyncLoading(false);
            setSyncStatus("FAILED");
        }
    };

    const docColumns = [
        {
            title: 'Fitxer',
            dataIndex: 'nom',
            key: 'nom',
            render: (text: string) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BiFile size={20} style={{ color: HomeColors.colors.color4 }} />
                    <span style={{ fontWeight: 500 }}>{text}</span>
                </div>
            )
        },
        {
            title: 'Mida',
            dataIndex: 'mida',
            key: 'mida',
            width: 100,
            render: (bytes: number) => {
                if (!bytes) return "0 KB";
                const kb = bytes / 1024;
                if (kb < 1024) return `${kb.toFixed(1)} KB`;
                return `${(kb / 1024).toFixed(1)} MB`;
            }
        },
        {
            title: 'Data Pujada',
            dataIndex: 'data_creacio',
            key: 'data_creacio',
            width: 150,
            render: (dateStr: string) => dateStr ? new Date(dateStr).toLocaleDateString('es-ES') : '-'
        },
        {
            title: 'Accions',
            key: 'actions',
            width: 100,
            align: 'center' as const,
            render: (_: any, record: any) => (
                <Popconfirm
                    title="Eliminar document"
                    description="Estàs segur que vols eliminar aquest recurs de la IA?"
                    onConfirm={() => handleDeleteDocument(record.id)}
                    okText="Sí"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                >
                    <Button type="text" danger icon={<BiTrash size={18} />} />
                </Popconfirm>
            )
        }
    ];

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={<div style={{ fontSize: '1.4rem', fontWeight: 700, color: HomeColors.colors.color4 }}>Gestió Completa de l'Aula</div>}
                open={visible}
                onCancel={handleCancel}
                footer={null}
                width={850}
                centered
                maskClosable={false}
            >
                <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)} size="large" centered>
                    {/* Tab 1: General Settings */}
                    <Tabs.TabPane tab="⚙️ Configuració i IA" key="1">
                        <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark="optional" style={{ marginTop: '15px' }}>
                            <div style={{ padding: '5px 0' }}>
                                <Row gutter={16}>
                                    <Col span={6}>
                                        <Form.Item label="ID" name="id">
                                            <Input disabled style={{ fontWeight: 'bold', textAlign: 'center' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="Nom de l'Aula" name="nom" rules={[{ required: true, message: 'El nom és obligatori' }]}>
                                            <Input placeholder="Ex: Programació Web" size="large" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                        <Form.Item label="Codi (Clau de registre)" name="codi" rules={[{ required: true, message: 'El codi és obligatori' }]}>
                                            <Input placeholder="Ex: M06-A" size="large" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item label="Context (Prompt inicial per a la IA)" name="context" rules={[{ required: true, message: 'El context és obligatori' }]}>
                                    <Input.TextArea rows={4} placeholder="Escriu les directrius inicials per a la IA. Per exemple: 'Ets un tutor concís de Java...'" style={{ resize: 'vertical' }} />
                                </Form.Item>

                                <Form.Item label="Administradors (Professors)" name="admins">
                                    <Select
                                        mode="multiple"
                                        placeholder="Selecciona els professors administradors..."
                                        options={profesors.map(p => ({ label: `${p.nom} ${p.cognoms}`, value: p.id }))}
                                        size="large"
                                    />
                                </Form.Item>
                            </div>

                            <Form.Item label="Imatge del Grup" name="imagen">
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                    <Upload
                                        beforeUpload={() => false}
                                        maxCount={1}
                                        showUploadList={false}
                                        onChange={(info) => {
                                            if (info.fileList.length > 0) {
                                                const file = info.fileList[0].originFileObj as File;
                                                const reader = new FileReader();
                                                reader.onloadend = () => setPreview(reader.result as string);
                                                reader.readAsDataURL(file);
                                            } else setPreview(null);
                                        }}
                                    >
                                        <Button icon={<UploadOutlined />}>Seleccionar Imatge</Button>
                                    </Upload>

                                    {preview && (
                                        <div style={{ border: '1px solid #ddd', padding: '5px', borderRadius: '4px', background: 'white' }}>
                                            <img src={preview} alt="preview" style={{ maxHeight: '80px', display: 'block' }} />
                                        </div>
                                    )}
                                </div>
                            </Form.Item>

                            <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                                <h4 style={{ marginBottom: '15px', fontWeight: 600 }}>⚙️ Característiques Actives</h4>
                                <Alert
                                    message="Avís important"
                                    description="Aquesta configuració s'aplica de forma global a l'aula. Si treballes amb sessions o tasques, aquestes podran heretar o personalitzar aquestes opcions."
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: '20px' }}
                                />
                                <Row gutter={[16, 16]} justify="center">
                                    <Col span={4}>
                                        <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', background: iaEnabled ? '#e6f7ff' : 'transparent', height: '100%' }}>
                                            <div style={{ marginBottom: 5, fontWeight: 500 }}>🤖 IA</div>
                                            <Form.Item name="ia" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                <Switch onChange={(checked) => setIaEnabled(checked)} />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={4}>
                                        <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', height: '100%' }}>
                                            <div style={{ marginBottom: 5, fontWeight: 500 }}>🙋 Dubtes</div>
                                            <Form.Item name="dudas" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                <Switch />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={4}>
                                        <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', height: '100%' }}>
                                            <div style={{ marginBottom: 5, fontWeight: 500 }}>🤝 Col·lab</div>
                                            <Form.Item name="colab" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                <Switch />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={4}>
                                        <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', height: '100%' }}>
                                            <div style={{ marginBottom: 5, fontWeight: 500 }}>📋 Copy/Paste</div>
                                            <Form.Item name="copypaste" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                <Switch />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={4}>
                                        <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', height: '100%' }}>
                                            <div style={{ marginBottom: 5, fontWeight: 500 }}>💼 DUAL</div>
                                            <Form.Item name="dual" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                <Switch />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={4}>
                                        <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', height: '100%' }}>
                                            <div style={{ marginBottom: 5, fontWeight: 500 }}>Inscripcions</div>
                                            <Form.Item name="inscripcionsObertes" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                <Switch checkedChildren="Sí" unCheckedChildren="No" />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={4}>
                                        <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', height: '100%' }}>
                                            <div style={{ marginBottom: 5, fontWeight: 500 }}>🎮 Joc Actiu</div>
                                            <Form.Item name="jocHabilitat" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                <Switch checkedChildren="Sí" unCheckedChildren="No" />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={4}>
                                        <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', height: '100%' }}>
                                            <Tooltip title="Activa o desactiva el bloqueig per pèrdua de focus tant en pràctiques com en exàmens als IDEs Theia i MySQL">
                                                <div style={{ marginBottom: 5, fontWeight: 500 }}>🛡️ Bloqueig Focus</div>
                                            </Tooltip>
                                            <Form.Item name="bloqueoFoco" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                <Switch checkedChildren="Sí" unCheckedChildren="No" />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={4}>
                                        <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', height: '100%' }}>
                                            <Tooltip title="Activa el Mode Examen a l'aula: oculta als alumnes les pestanyes de Pràctiques i Activitats i els exàmens no actius">
                                                <div style={{ marginBottom: 5, fontWeight: 500 }}>📝 Mode Examen</div>
                                            </Tooltip>
                                            <Form.Item name="isExamen" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                <Switch checkedChildren="Sí" unCheckedChildren="No" />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={6}>
                                        <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', height: '100%' }}>
                                            <Tooltip title="Selecciona la infraestructura para los IDEs de esta aula: Fargate estándar, Fargate ligero o Instancia EC2 dedicada con arranque instantáneo y pausa.">
                                                <div style={{ marginBottom: 5, fontWeight: 500 }}>🏗️ Arquitectura IDE</div>
                                            </Tooltip>
                                            <Form.Item name="ideArchitecture" style={{ marginBottom: 0 }}>
                                                <Select size="small" style={{ width: '100%' }}>
                                                    <Select.Option value="fargate_standard">
                                                        <span>📦 Fargate Estándar</span>
                                                    </Select.Option>
                                                    <Select.Option value="fargate_light">
                                                        <span>⚡ Fargate Light</span>
                                                    </Select.Option>
                                                    <Select.Option value="ec2_docker">
                                                        <span>🚀 EC2 Docker (Rápido)</span>
                                                    </Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </div>
                                    </Col>
                                </Row>

                                {iaEnabled && (
                                    <div style={{ marginTop: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', borderLeft: `5px solid ${HomeColors.colors.color4}`, boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.02)' }}>
                                        <h5 style={{ marginBottom: '12px', fontWeight: 600 }}>Ajustos de la IA</h5>
                                        <Row gutter={16} style={{ marginBottom: 15 }}>
                                            <Col span={24}>
                                                <Form.Item label="Model de Lenguatge (LLM)" name="tipo" rules={[{ required: true, message: 'Selecciona un model' }]}>
                                                    <Select
                                                        placeholder="Selecciona el model d'IA"
                                                        options={[
                                                            { value: 'llama', label: 'Meta Llama 3' },
                                                            { value: 'chatgpt', label: 'OpenAI ChatGPT' },
                                                            { value: 'agent', label: 'AWS Bedrock Agent (Knowledge Bases / RAG)' },
                                                        ]}
                                                        size="large"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={[24, 16]}>
                                            <Col span={8}>
                                                <Form.Item label="Tokens Màxims" name="tokens" style={{ marginBottom: 0 }} tooltip="Llargada de la resposta genèrica">
                                                    <Slider min={100} max={800} step={50} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item label="Temperatura" name="temp" style={{ marginBottom: 0 }} tooltip="Creativitat (0 = conservador, 1 = creatiu)">
                                                    <Slider min={0.0} max={1.0} step={0.1} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item label="TopP" name="topp" style={{ marginBottom: 0 }} tooltip="Diversitat de vocabulari">
                                                    <Slider min={0.0} max={1.0} step={0.1} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "30px", borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                <Button onClick={handleCancel} size="large">
                                    Cancel·lar
                                </Button>
                                <Button
                                    loading={loading}
                                    htmlType="submit"
                                    type="primary"
                                    size="large"
                                    icon={<BiCheck />}
                                    style={{ backgroundColor: HomeColors.colors.color4, borderColor: HomeColors.colors.color4 }}
                                >
                                    Guardar Canvis
                                </Button>
                            </div>
                        </Form>
                    </Tabs.TabPane>

                    {/* Tab 2: Custom Knowledge Base Documents */}
                    <Tabs.TabPane tab="📁 Base de Coneixements (IA)" key="2" disabled={element.id === 0 || element.id === "0"}>
                        {element.id === 0 || element.id === "0" ? (
                            <div style={{ padding: '40px 0', textAlign: 'center' }}>
                                <Alert
                                    message="Acció Requerida"
                                    description="Has de guardar la nova aula abans de poder pujar o gestionar fitxers per a la base de coneixements de la IA."
                                    type="info"
                                    showIcon
                                />
                            </div>
                        ) : (
                            <div style={{ padding: '10px 0' }}>
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ fontWeight: 600, marginBottom: '8px' }}>📂 Documents i Recursos de Referència</h4>
                                    <p style={{ color: '#666', fontSize: '0.95rem' }}>
                                        Puja documents de classe (apunts, PDFs de teoria, codis font de referència). La IA farà servir aquests fitxers directament per a respondre els dubtes dels alumnes de forma personalitzada i precisa.
                                    </p>
                                </div>

                                {/* Drag and Drop Upload Area */}
                                <div style={{ marginBottom: '25px' }}>
                                    <Upload.Dragger
                                        accept=".pdf,.txt,.md,.docx,.zip,.java"
                                        showUploadList={false}
                                        beforeUpload={async (file) => {
                                            try {
                                                setDocsLoading(true);
                                                await PostAulaDocument(element.id + "", file);
                                                message.success(`S'ha pujat "${file.name}" correctament! 🎉`);
                                                fetchDocuments();
                                            } catch (err: any) {
                                                message.error(err.message || `Error en pujar "${file.name}".`);
                                            } finally {
                                                setDocsLoading(false);
                                            }
                                            return false; // Prevent auto upload since we did it manually
                                        }}
                                    >
                                        <p className="ant-upload-drag-icon">
                                            <InboxOutlined style={{ color: HomeColors.colors.color4 }} />
                                        </p>
                                        <p className="ant-upload-text" style={{ fontSize: '1.05rem', fontWeight: 500 }}>
                                            Arrossega fitxers aquí o fes clic per a seleccionar fitxers
                                        </p>
                                        <p className="ant-upload-hint">
                                            Formats admesos: PDF, TXT, Markdown, Word o codi font (.java). Mida màx: 15MB.
                                        </p>
                                    </Upload.Dragger>
                                </div>

                                {/* URL Reference Input */}
                                 <div style={{
                                     background: '#ffffff',
                                     border: '1px solid #e8e8e8',
                                     borderRadius: '12px',
                                     padding: '20px',
                                     marginBottom: '25px',
                                     boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                 }}>
                                     <h5 style={{ fontWeight: 600, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                         <BiLink size={20} style={{ color: HomeColors.colors.color4 }} />
                                         Afegir enllaç URL o lloc web de referència
                                     </h5>
                                     <p style={{ color: '#777', fontSize: '0.85rem', marginBottom: '15px' }}>
                                         El servidor descarregarà i processarà automàticament el contingut de text d'aquesta pàgina per a la base de coneixements.
                                     </p>
                                     <div style={{ display: 'flex', gap: '10px' }}>
                                         <Input 
                                             placeholder="Ex: https://viquipedia.org/wiki/Java" 
                                             value={urlInput}
                                             onChange={(e) => setUrlInput(e.target.value)}
                                             size="large"
                                             style={{ flex: 1, borderRadius: '8px' }}
                                         />
                                         <Button 
                                             type="primary" 
                                             onClick={handleUploadUrl}
                                             loading={urlLoading}
                                             size="large"
                                             style={{
                                                 backgroundColor: HomeColors.colors.color4,
                                                 borderColor: HomeColors.colors.color4,
                                                 borderRadius: '8px',
                                                 fontWeight: 600
                                             }}
                                         >
                                             Afegir URL
                                         </Button>
                                     </div>
                                 </div>

                                {/* Sync Status panel */}
                                <div style={{ background: '#f8f9fa', border: '1px solid #e8e8e8', borderRadius: '12px', padding: '18px', marginBottom: '25px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ flex: 1, minWidth: '250px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                            <span style={{ fontWeight: 600, fontSize: '1.05rem' }}>Sincronització amb AWS Bedrock</span>
                                            {syncStatus === "COMPLETE" && <Tag color="success">Sincronitzat</Tag>}
                                            {syncStatus === "IN_PROGRESS" && <Tag color="processing">Sincronitzant...</Tag>}
                                            {syncStatus === "FAILED" && <Tag color="error">Error</Tag>}
                                            {syncStatus === "NOT_SYNCED" && <Tag color="default">Pendent de Sincronitzar</Tag>}
                                        </div>
                                        <p style={{ color: '#777', fontSize: '0.85rem', margin: 0 }}>
                                            Després de pujar o esborrar fitxers, prem Sincronitzar per tal que la IA actualitzi la seva base de coneixements vectorials.
                                        </p>
                                        {syncLoading && (
                                            <div style={{ marginTop: '10px' }}>
                                                <Progress percent={syncProgress} status="active" strokeColor={HomeColors.colors.color4} />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<BiSync size={20} className={syncLoading ? "spin-animation" : ""} />}
                                            loading={syncLoading}
                                            onClick={handleSyncBedrock}
                                            style={{ backgroundColor: HomeColors.colors.color4, borderColor: HomeColors.colors.color4, display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, borderRadius: '8px' }}
                                        >
                                            Sincronitzar IA
                                        </Button>
                                    </div>
                                </div>

                                {/* Uploaded Documents Table */}
                                <h5 style={{ fontWeight: 600, marginBottom: '12px' }}>📋 Documents de l'Aula <Badge count={documents.length} style={{ backgroundColor: HomeColors.colors.color4 }} /></h5>
                                <Table
                                    columns={docColumns}
                                    dataSource={documents}
                                    rowKey="id"
                                    loading={docsLoading}
                                    size="middle"
                                    pagination={{ pageSize: 5 }}
                                    style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}
                                />
                            </div>
                        )}
                    </Tabs.TabPane>
                </Tabs>
            </Modal>
            
            {/* Embedded styles for spinning sync icon */}
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .spin-animation {
                    animation: spin 2s linear infinite;
                }
            `}</style>
        </ConfigProvider>
    );
};

export default AulaModal;
